import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-balham.css";
import { AgGridReact, AgGridReactProps } from "ag-grid-react";
import "ag-grid-enterprise";
import * as React from "react";
import styled from "styled-components";
import { GridLoadingOverlay } from "./GridLoadingOverlay";
import GridStatusBarTotalRows from "./GridStatusBarTotalRows";
import {
  GridApi,
  Column,
  ColDef,
  ColumnState,
  StatusPanelDef,
  ITooltipParams,
  ColumnApi,
  ColGroupDef,
  CellClassParams,
  EditableCallbackParams,
  GridReadyEvent,
  ModelUpdatedEvent
} from "ag-grid-community";
import { GridTooltip } from "./GridTooltip";
import GridStatusBarResetButton from "./GridStatusBarResetButton";
import { isEqual } from "lodash";
import { SubHeader } from "../styled/SubHeader";
import { getGridTranslations } from "../../../utilities/gridUtils";
import "styles/agGridStyles.scss";
import { LicenseManager } from "ag-grid-enterprise";
import { agGridConfiguration } from "configuration/agGrid";

if (agGridConfiguration.licenseKey) {
  LicenseManager.setLicenseKey(agGridConfiguration.licenseKey);
}

interface ShowStatusBar {
  showRowCount?: boolean;
  showResetButton: boolean;
}

export interface DefaultFilterModel {
  [key: string]: { filterType: string; values: any[] };
}

export interface GridProps extends Omit<AgGridReactProps, "defaultColDef"> {
  title?: string;
  titleHeadingLevel?: 1 | 2 | 3 | 4 | 5 | 6;
  autosizeCols?: boolean;
  disableColumnAutoSize?: boolean;
  keepEditingIfFocusIsLost?: boolean;
  showPointerOnRowHover?: boolean;
  showStatusBar?: ShowStatusBar;
  gridCalculatedMaxWidth?: number;
  colDefDefault?: ColDef;
  disableResize: boolean;
  defaultSort?: ColumnState;
  defaultFilterModel?: DefaultFilterModel;
  isSortPersisted?: boolean;
  isOnlyResetSorting?: boolean;
  t: (text: string) => string;
}

const GridContainer = styled.div<{ showPointerOnRowHover?: boolean; maxWidth?: number }>`
  display: flex !important;
  flex-grow: 1 !important;
  flex-direction: column !important;
  width: 100%;
  max-width: ${(props) => (props.maxWidth ? props.maxWidth + "px" : undefined)};
  .ag-row-hover {
    cursor: ${(props) => (props.showPointerOnRowHover ? "pointer !important" : "inherited")};
  }
`;

const GridInnerContainer = styled.div`
  display: flex !important;
  flex-grow: 1 !important;
  margin-top: ${(props) => props.theme.sizes.s};
`;

export class Grid extends React.PureComponent<GridProps> {
  private api: GridApi | undefined;
  private previousCount: React.MutableRefObject<number | null>;

  constructor(props: GridProps, ctx: any) {
    super(props, ctx);
    this.api = undefined;
    this.previousCount = React.createRef<number | null>();
  }

  static defaultProps = {
    disableResize: false,
    isOnlyResetSorting: false,
    isSortPersisted: false
  };

  componentWillUnmount(): void {
    window.removeEventListener("resize", this.resizeGrid);
  }

  render(): React.ReactNode {
    const { title, colDefDefault, ...agGridProps } = this.props;

    const columnDefs = this.applyGlobalStyleRules(agGridProps.columnDefs ?? null);

    return (
      <GridContainer
        showPointerOnRowHover={this.props.showPointerOnRowHover}
        maxWidth={this.props.gridCalculatedMaxWidth}
      >
        {this.renderOptionalTitle(title)}
        <GridInnerContainer>
          <div className="ag-theme-balham" style={{ flexGrow: 1 }}>
            <AgGridReact
              rowHeight={36}
              tooltipShowDelay={0}
              localeText={getGridTranslations(this.props.t)}
              defaultColDef={{
                resizable: true,
                sortable: true,
                minWidth: 100,
                tooltipComponent: "customTooltip",
                tooltipValueGetter: (params) => {
                  if (params.valueFormatted) {
                    return params.valueFormatted;
                  }
                  return params.value;
                },
                ...colDefDefault
              }}
              {...agGridProps}
              columnDefs={columnDefs}
              loadingOverlayComponent={GridLoadingOverlay}
              onGridReady={(params: GridReadyEvent) => {
                this.api = params.api;

                if (this.props.autosizeCols) {
                  params.api.sizeColumnsToFit();
                }

                if (agGridProps.onGridReady) {
                  agGridProps.onGridReady(params);
                }
                if (!this.props.disableResize) {
                  window.addEventListener("resize", this.resizeGrid);
                }
                if (this.props.defaultFilterModel) {
                  params.api.setFilterModel(this.props.defaultFilterModel);
                  params.api.onFilterChanged();
                }
              }}
              onFirstDataRendered={(params) => {
                if (agGridProps.onFirstDataRendered) {
                  agGridProps.onFirstDataRendered(params);
                }

                if (this.props.defaultSort) {
                  const sortModel = params.columnApi.getColumnState();
                  if (this.props.isSortPersisted) {
                    const defaultSortModel = sortModel.map((model) => {
                      if (model.colId === this.props.defaultSort?.colId) {
                        return {
                          ...model,
                          sort: this.props.defaultSort?.sort
                        };
                      }
                      return {
                        ...model,
                        sort: null
                      };
                    });
                    setDefaultSortCol(params.api, defaultSortModel);
                  } else {
                    setDefaultSortCol(params.api, sortModel);
                  }
                }

                if (this.props.defaultFilterModel) {
                  setDefaultFilterModel(params.api, this.props.defaultFilterModel);
                }

                this.handleResetButtonStateRegardingFilters(params.api);
                this.handleResetButtonStateRegardingSorting(params.columnApi, params.api);
              }}
              onFilterChanged={(params) => {
                this.handleResetButtonStateRegardingFilters(params.api);
              }}
              onSortChanged={(params) => {
                this.handleResetButtonStateRegardingSorting(params.columnApi, params.api);
              }}
              stopEditingWhenCellsLoseFocus={!this.props.keepEditingIfFocusIsLost}
              onRowDataChanged={(params) => {
                if (!this.props.disableColumnAutoSize) {
                  params.columnApi.autoSizeAllColumns();
                }
              }}
              onRowDataUpdated={(params) => {
                if (!this.props.disableColumnAutoSize) {
                  params.columnApi.autoSizeAllColumns();
                }
              }}
              onModelUpdated={(params) => {
                if (this.props.showStatusBar) {
                  this.setTotalRowCount(params);
                }
              }}
              components={{
                statusBarTotalRows: GridStatusBarTotalRows,
                statusBarResetButton: GridStatusBarResetButton,
                customTooltip: GridTooltip
              }}
              statusBar={
                this.props.showStatusBar
                  ? {
                      statusPanels: getStatusPanels(this.props)
                    }
                  : undefined
              }
            />
          </div>
        </GridInnerContainer>
      </GridContainer>
    );
  }

  private resizeGrid = (): any => {
    if (this.api) {
      setTimeout(() => {
        this.api?.sizeColumnsToFit();
      });
    }
  };

  private applyGlobalStyleRules(
    columnDefs: (ColGroupDef | ColDef)[] | null
  ): (ColDef | ColGroupDef)[] | null {
    if (!columnDefs) {
      return columnDefs;
    }

    return columnDefs.map((def: ColDef) => ({
      ...def,
      cellClassRules: {
        ...def.cellClassRules,
        readonly: (params: CellClassParams) => {
          const editable = params?.colDef?.editable;

          if (typeof editable === "boolean") {
            return !editable;
          }

          if (typeof editable === "function") {
            return !editable(params as unknown as EditableCallbackParams);
          }

          return true;
        }
      }
    }));
  }

  private renderOptionalTitle(title: string | undefined): React.ReactNode {
    if (!title) {
      return null;
    }

    return <SubHeader text={title} />;
  }

  private setTotalRowCount(params: ModelUpdatedEvent) {
    const rowModelType = params.api.getModel().getType();
    let count: undefined | number;
    if (rowModelType === "infinite") {
      count = params.api.getInfiniteRowCount();
    } else {
      count = params.api.getDisplayedRowCount();
      setStatusBarRowCount(params.api, count);
    }

    // getInfiniteRowCount() returns 1 when new data is being loaded so hacky logic not to show the 1 every time data is being loaded
    if (
      (rowModelType === "infinite" &&
        count !== undefined &&
        (this.previousCount.current === null || this.previousCount.current === 1) &&
        count !== 1) ||
      (this.previousCount.current === 1 && count === 1) ||
      (this.previousCount.current !== 1 && count !== undefined && count !== 1)
    ) {
      setStatusBarRowCount(params.api, count);
    }
    this.previousCount.current = count ? count : null;
  }

  private handleResetButtonStateRegardingSorting(columnApi: ColumnApi, gridApi: GridApi) {
    const columnState = columnApi.getColumnState();
    let hasChanged = false;
    for (const column of columnState) {
      if (
        column.colId === this.props.defaultSort?.colId &&
        column.sort !== this.props.defaultSort?.sort
      ) {
        hasChanged = true;
        this.enableResetButton(gridApi, true);
        break;
      }
    }

    if (!hasChanged) {
      this.enableResetButton(gridApi, false);
    }
  }

  private handleResetButtonStateRegardingFilters(gridApi: GridApi) {
    const filterModel = gridApi.getFilterModel();
    if (this.props.defaultFilterModel && !isEqual(filterModel, this.props.defaultFilterModel)) {
      this.enableResetButton(gridApi, true);
    } else if (!this.props.defaultFilterModel && Object.keys(filterModel).length > 0) {
      this.enableResetButton(gridApi, true);
    } else {
      this.enableResetButton(gridApi, false);
    }
  }

  private enableResetButton(gridApi: GridApi, isEnabled: boolean) {
    const resetButtonComponent = getStatusPanelComponent<GridStatusBarResetButton>(
      gridApi,
      "statusBarResetButton"
    );
    resetButtonComponent?.setIsEnabled(isEnabled);
  }
}

export interface AgGridSortModel {
  sortColumn: string;
  isAsc: boolean;
}
export function getSortModel(sortModel: ColumnState[]): AgGridSortModel | undefined {
  if (sortModel.length > 0 && sortModel[0].colId) {
    return {
      sortColumn: sortModel[0].colId,
      isAsc: sortModel[0].sort === "asc" ? true : false
    };
  }
  return undefined;
}

export const setStatusBarRowCount = (gridApi: GridApi, rowCount: number): void => {
  const totalRowsComponent = getStatusPanelComponent<GridStatusBarTotalRows>(
    gridApi,
    "statusBarTotalRows"
  );
  totalRowsComponent?.setRowCount(rowCount);
};

const setDefaultSortCol = (gridApi: GridApi, sortModel: ColumnState[]) => {
  const resetButtonComponent = getStatusPanelComponent<GridStatusBarResetButton>(
    gridApi,
    "statusBarResetButton"
  );
  resetButtonComponent?.setDefaultSortModel(sortModel);
};

const setDefaultFilterModel = (
  gridApi: GridApi,
  filterModel: Record<string, { filterType: string; values: any[] }>
) => {
  const resetButtonComponent = getStatusPanelComponent<GridStatusBarResetButton>(
    gridApi,
    "statusBarResetButton"
  );
  resetButtonComponent?.setDefaultFilterModel(filterModel);
};

const getStatusPanelComponent = <T,>(gridApi: GridApi, key: string): T | undefined => {
  const statusBarComponent = gridApi.getStatusPanel(key) as any;
  return statusBarComponent as unknown as T;
};

export const getTotalWidth = (columns: Column[]): number => {
  let total = 0;
  columns.forEach((column) => {
    total += column.getActualWidth();
  });
  return total;
};

interface ColHeaderAndHeaderTooltip {
  headerName: string;
  headerTooltip: string;
}

export const createHeaderAndHeaderTooltip = (
  headerName: string,
  headerTooltip?: string
): ColHeaderAndHeaderTooltip => {
  return {
    headerName: headerName,
    headerTooltip: headerTooltip ?? headerName
  };
};

export const getDefaultColumnSort = (
  isAsc: boolean | undefined,
  sortColumn: string | null | undefined
): ColumnState => {
  return {
    colId: sortColumn ? sortColumn : "",
    sort: isAsc ? "asc" : "desc"
  };
};

const getStatusPanels = (props: GridProps): StatusPanelDef[] => {
  const statusPanels: StatusPanelDef[] = [];
  if (props.showStatusBar?.showRowCount) {
    statusPanels.push({
      statusPanel: "statusBarTotalRows",
      key: "statusBarTotalRows",
      statusPanelParams: {
        t: props.t
      }
    });
  }

  if (props.showStatusBar?.showResetButton) {
    statusPanels.push({
      statusPanel: "statusBarResetButton",
      key: "statusBarResetButton",
      statusPanelParams: {
        isOnlyResetSorting: props.isOnlyResetSorting,
        disableResize: props.disableResize,
        t: props.t
      },
      align: "left"
    });
  }
  return statusPanels;
};

type CreateCellRendererField = {
  field: string;
  tooltipValueGetter: ((params: ITooltipParams) => string) | undefined;
};

export const createCellRendererField = (field: string): CreateCellRendererField => {
  return { field: field, tooltipValueGetter: () => "" };
};
